import UtilsService from "app/services/utils/utils.service";
const NS = 'TableLabelService';

/**
 * Description:
 * A service for building labels on the floorplan's tables.
 * Gets loaded as part of 'floorplan' module.
 *
 * External dependencies:
 * - moment
 * - Snap SVG
 * - underscore
 */

export default function TableLabelService(PlanConst, SnapService, tableHelpersService, SnapConst, Group) {
    "ngInject";

    const THS = tableHelpersService;

    /**
     * PUBLIC FUNCTIONS
     */
    const service = {
        buildLabel: buildLabel
    };


    /**
     * @param {*} target - Snap table object
     * @param {*} time - current time (moment object)
     * @return - array of plain text, which will be used for comparison to avoid repeated logic
     */
    function buildLabel(target, time, mode, model, prevLabelLines, snapInstance, service, nextService) {
        // console.log(target, time, mode, model, prevLabelLines, snapInstance)
        window.renderedTableLabels.total++;
        // Retrieve current label content
        // Check private function, determine if basic label (name + capacity) will be 1 or 2 lines
        const pf = _getTablePrivateFunction(mode, time, service, nextService);

        //Table label in 2 lines
        const content = _getLabelContent(model);

        if (pf != null) {
            content.lines.push(pf.name);
            content.lines.push('(P' + pf.people + ')');
        } // additional lines related to private function

        const lines = _getTextAsSVGNodes(snapInstance, content.lines);

        // Compare with previous labelLines
        const equals = UtilsService.areSameStringArrays(prevLabelLines, content.lines);
        if (equals && mode === PlanConst.Mode.Bookings) {
            window.renderedTableLabels.skipped++;
            return null;
        }

        const labelLinesToReturn = content.lines;

        const label = _createNewLabel(target, snapInstance);

        //Detect table type
        const isPlanTable = target.getSGroup().hasClass('full');

        //Define max width
        const maxSize = _getTableMaxSize(isPlanTable, model, mode);

        target.addElement(label, 'label');

        _truncateLabelLines(label, lines, maxSize.w);
        _verticallyPositionLabelLines(lines);
        _centerLabelInTable(isPlanTable, label, target, model);
        _rotateLabelToMatchTable(mode, model, label);

        label.getSGroup().removeClass('transparent');

        return labelLinesToReturn;
    }

    /**
     * PRIVATE FUNCTIONS BELOW HERE
     */

    function _createNewLabel(target, snapInstance) {
        //Remove previous label
        if (target.getElement('label')) {
            target.removeElement('label');
        }
        //Build new label
        var label = new Group(snapInstance, 'label');
        label.getSGroup().addClass('transparent');

        return label;
    }

    /**
     * Returns the current service (if it is a private function) or the next service (if that is a private function)
     * @param {*} time - current time (moment object)
     */
    function _getTablePrivateFunction(mode, time, service, nextService) {
        var pf = null;
        if (mode === PlanConst.Mode.Bookings) {
            if (THS.isRunningPrivateFun(service, time)) {
                pf = service;
            } else if (THS.isApproachingPrivateFun(nextService, time)) {
                pf = nextService;
            }
        }

        return pf;
    }


    /**
     * Returns object with info about the table, to be used with label.
     * @param {*} model - table model (see plan -> model -> table.js)
     */
    function _getLabelContent(model) {
        var content = {};
        content.name = 'T' + (model.name ? model.name : '');
        content.status = model.online ? '*' : '';
        content.status += model.shared ? '^' : '';
        content.status += model.walkinOnly ? '~' : '';
        content.capacity = 'C' + model.capacity;
        content.value = content.name + content.status + ' ' + content.capacity;
        content.lines = [
            content.name,
            content.status + ' ' + content.capacity
        ];
        return content;
    }

    /**
     * Converts plain text into SVG nodes of text.
     * The prop 'snapInstance' refers to the Snap SVG library.
     * @param {*} textLines array of plain text. Each item will be treated as a separate line.
     */
    function _getTextAsSVGNodes(snapInstance, textLines) {
        var nodeLines = [];
        _.each(textLines, function(l) {
            var textNode = snapInstance.text(0, 0, l);
            nodeLines.push({
                el: textNode
            });
        }, this);

        return nodeLines;
    }

    /**
     * Returns an object with max width and height dimensions
     * @param {*} isPlanTable - a plain table is based on whether of not it contains the 'full' css class
     */
    function _getTableMaxSize(isPlanTable, model, mode) {
        var maxSize = {};
        if (isPlanTable) {
            if (model.shape === SnapConst.Shapes.Circle) {
                maxSize.w = Math.floor(Math.sqrt(Math.pow(THS.getSize(model, true).w, 2) / 2));
                maxSize.h = maxSize.w;
            } else {
                var boundingBox = THS.rootBBox(model);
                if (mode === PlanConst.Mode.Bookings) {
                    //No more rotation action available, i can use the actual rotated size (may be bigger)
                    if (model.rotation === 90 || model.rotation === 180) {
                        maxSize.w = boundingBox.h;
                        maxSize.h = boundingBox.w;
                    }
                    if (model.rotation === 0 || model.rotation === 360) {
                        maxSize.w = boundingBox.w;
                        maxSize.h = boundingBox.h;
                    }
                } else {
                    maxSize.w = Math.min(boundingBox.h, boundingBox.w);
                    maxSize.h = maxSize.w;
                }
            }
        } else {
            var tSize = THS.getSize(model, false);
            if (model.shape === SnapConst.Shapes.Circle) {
                maxSize.w = Math.floor(Math.sqrt(Math.pow(tSize.w, 2) / 2)); //circle has less with available
                maxSize.h = maxSize.w;
            } else {
                maxSize.w = tSize.w - 5; //a bit of margins
                maxSize.h = tSize.w - 5;
            }
        }

        return maxSize;
    }

    /**
     * Adds 'ellipses' text truncation based on width
     * @param {*} label
     * @param {*} maxWidth
     */
    function _truncateLabelLines(label, lines, maxWidth) {
        _.each(lines, function(line, n) {
            label.addElement(line.el, 'line' + n);
            SnapService.setEllipsis(line.el, maxWidth);
            SnapService.applyEllipsis(line.el);
            line.width = line.el.node.getComputedTextLength();
        });
    }

    /**
     * Spaces out lable text vertically
     * @param {*} lines
     */
    function _verticallyPositionLabelLines(lines) {
        var lineHeight = 20;
        var labelWidth = _.max(lines, function(line) {
            return line.width;
        }).width;
        _.each(lines, function(line, n) {
            SnapService.place(line.el,
                (labelWidth - line.width) / 2, //w center line
                n * lineHeight //h center line
            );
        });
    }

    /**
     * Positions the label in the center of the table
     * @param {*} isPlanTable - a plain table is based on whether of not it contains the 'full' css class
     * @param {*} label - Snap label object
     * @param {*} target - Snap table object
     */
    function _centerLabelInTable(isPlanTable, label, target, model) {
        var shape = THS.getSize(model, isPlanTable);
        var ls = SnapService.getShape(label);
        target.getElement('label').shape = ls;
        var groupBoundingBox = {
            x: ls.x,
            y: ls.y,
            w: ls.w,
            h: ls.h
        };
        SnapService.place(label, (shape.w - groupBoundingBox.w) / 2, (shape.h - groupBoundingBox.h) / 2);
    }

    /**
     * If rotation is supported, rotates the label to match it
     * @param {*} label - Snap label object
     */
    function _rotateLabelToMatchTable(mode, model, label) {
        if (mode === PlanConst.Mode.Bookings && model.rotation) {
            SnapService.rotate(label, (model.rotation > 0 ? -model.rotation : Math.abs(model.rotation)));
        }
    }


    /**
     * Return service
     */
    return service;
}
